import { LinearProgress } from "@material-ui/core";
import React, { useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";

import API from "../../../../../../app/utils/api";
import { resolveApiMessageSeverity } from "../../../../../../app/utils/apiResponseMessages";
import { formatExcelDate } from "../../../../../../app/utils/process.excel.helper";
import {
  readSiteIdEffortFromWorksheetCell,
  resolveSiteIdEffort,
} from "../../../../../../app/utils/siteIdEffort";
import FileUploader from "../../../../../../app/components/file.uploader";
import CustomModal from "../../../../../../app/components/modal.no.close";
import { useSnackbar } from "../../../../../../app/contexts/snackbar.context";
import { useStudyPlan } from "../../../../../../app/contexts/study.context";
import PrimaryButton from "../../../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../../../app/components/buttons/outlined";
import WarningDialog from "../../../../../../app/components/warning.dialog";
import { ACTIVATION_PROFILES_WARNING_MESSAGE_UPLOAD } from "../../../../../../app/components/warning.dialog/messages";
import { validateActivationProfilesTable } from "../../../../../../app/hooks/activation.profiles/useActivationProfilesValidation";

const STUDY = "STUDY";
const DURATION = "duration";
const validatePauseEnrollmentFields = (pausePatient, pauseDays) => {
  const errors = [];
  
  // Check pause patient field
  if (pausePatient !== null && pausePatient !== undefined && pausePatient !== "") {
    const pausePatientNum = Number(pausePatient);
    if (isNaN(pausePatientNum) || pausePatientNum <= 0 || !Number.isInteger(pausePatientNum)) {
      errors.push("Pause Enrollment After Participant # must be bigger than 0.");
    }
  }
  
  // Check pause days field
  if (pauseDays !== null && pauseDays !== undefined && pauseDays !== "") {
    const pauseDaysNum = Number(pauseDays);
    if (isNaN(pauseDaysNum) || pauseDaysNum <= 0 || !Number.isInteger(pauseDaysNum)) {
      errors.push("Resume Screening After (days) must be bigger than 0.");
    }
  }
  
  return errors;
};

// Constants for parameter and milestone names
const STUDY_PLAN_CONSTANTS = {
  PARAMETERS: {
    SITES_TO_ACTIVATE: 'SITES_TO_ACTIVATE',
    SUBJECTS_TO_SCREEN: 'SUBJECTS_TO_SCREEN',
  },
  MILESTONES: {
    NUM_SUBJECTS_ENROLLED: 'NUM_SUBJECTS_ENROLLED',
    NUM_SUBJECTS_TREATED: 'NUM_SUBJECTS_TREATED',
    NUM_SUBJECTS_COMPLETED: 'NUM_SUBJECTS_COMPLETED',
  },
  TABLE_NAMES: {
    COUNTRY_INPUTS: 'Country Inputs',
    MILESTONES: 'Milestones',
    ACTIVATION_PROFILES: 'Activation Profiles',
    ENROLLMENT_SEASONAL_VARIATIONS: 'Enrollment Seasonal Variations',
    SITE_FATIGUE: 'Site Fatigue',
  },
};

const CARD_PERCENTAGES = [
  'CARD_1_PERCENT', 'CARD_2_PERCENT', 'CARD_3_PERCENT', 'CARD_4_PERCENT', 
  'CARD_5_PERCENT', 'CARD_6_PERCENT', 'CARD_7_PERCENT', 'CARD_8_PERCENT', 
  'CARD_9_PERCENT', 'CARD_10_PERCENT', 'CARD_11_PERCENT'
];

const extractAdditionalFields = (sheetData, studyPlan) => {
  // Helper function to get value or null if undefined
  const getValueOrNull = (value) => (value === undefined ? null : value);

  // Helper function to convert date string to milliseconds
  const convertToMillis = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) ? date.getTime() : null;
  };

  const calculateSitesToActivate = (sheetData) => {
    // First check cell D10 (index [9][3])
    const cellD10Value = sheetData[9]?.[3];

    if (cellD10Value !== null && cellD10Value !== undefined && cellD10Value !== "") {
      const numValue = Number(cellD10Value);
      if (!isNaN(numValue)) {
        return numValue;
      }
    }
    // Sum up values from F29 to F58
    let sum = 0;
    for (let i = 28; i < 58; i++) {
      const value = sheetData[i]?.[5]; // Column F is index 5
      if (value !== null && value !== undefined && value !== "") {
        const numValue =
          typeof value === "number" ? value : Number(value.toString().trim());
        if (!isNaN(numValue)) {
          sum += numValue;
        }
      }
    }
    return sum > 0 ? sum : null;
  };

  const sitesToActivate = studyPlan.parameters?.find(
    (param) => param.parameterName === STUDY_PLAN_CONSTANTS.PARAMETERS.SITES_TO_ACTIVATE,
  );
  const subjectsToScreen = studyPlan.parameters?.find(
    (param) => param.parameterName === STUDY_PLAN_CONSTANTS.PARAMETERS.SUBJECTS_TO_SCREEN,
  );
  const subjectsToEnroll = studyPlan.milestones?.find(
    (milestone) => milestone.milestoneName === STUDY_PLAN_CONSTANTS.MILESTONES.NUM_SUBJECTS_ENROLLED,
  );
  const subjectsToTreat = studyPlan.milestones?.find(
    (milestone) => milestone.milestoneName === STUDY_PLAN_CONSTANTS.MILESTONES.NUM_SUBJECTS_TREATED,
  );
  const subjectsToComplete = studyPlan.milestones?.find(
    (milestone) => milestone.milestoneName === STUDY_PLAN_CONSTANTS.MILESTONES.NUM_SUBJECTS_COMPLETED,
  );

  // Extract fields from Excel
  const extractedData = {
    // Values from studyPlan
    studyPlanId: studyPlan.studyPlanId,
    studyId: studyPlan.studyId,
    studyPlanType: studyPlan.studyPlanType,
    cohortType: studyPlan.cohortType,

    // Study Details (root level)
    studyPlanName: getValueOrNull(sheetData[4]?.[3]), // Cell D5
    studyPlanDescr: getValueOrNull(sheetData[5]?.[3]), // Cell D6

    // General Parameters (root level)
    croProjectStartDateMillis: convertToMillis(
      getValueOrNull(formatExcelDate(sheetData[4]?.[9])),
    ), // Cell J5
    finalProtocolStartDateMillis: convertToMillis(
      getValueOrNull(formatExcelDate(sheetData[5]?.[9])),
    ), // Cell J6
    overallEnrollmentDuration: getValueOrNull(sheetData[7]?.[3]), // Cell D8
    enrollmentRate: getValueOrNull(sheetData[8]?.[3]), // Cell D9
    screeningPeriodDuration: getValueOrNull(sheetData[12]?.[3]), // Cell D13
    treatmentDuration: getValueOrNull(sheetData[13]?.[3]), // Cell D14
    followUpDuration: getValueOrNull(sheetData[14]?.[3]), // Cell D15
    screenFailRate: getValueOrNull(sheetData[17]?.[7]), // Cell H18
    dropOutRate: getValueOrNull(sheetData[18]?.[7]), // Cell H19
    pauseEnrollmentAfterPatient: getValueOrNull(sheetData[22]?.[3]), // Cell D23
    pauseEnrollmentDays: getValueOrNull(sheetData[23]?.[3]), // Cell D24

    // Parameters object
    parameters: [
      {
        studyPlanParameterId: subjectsToScreen.studyPlanParameterId,
        parameterScope: STUDY,
        parameterId: 13,
        lowerBound: getValueOrNull(sheetData[16]?.[3]), // Cell D17
        upperBound: null,
      },
      {
        studyPlanParameterId: sitesToActivate?.studyPlanParameterId,
        parameterScope: STUDY,
        parameterId: 12,
        lowerBound: calculateSitesToActivate(sheetData),
        upperBound: null,
      },
    ],

    // Milestones object
    milestones: [
      {
        studyPlanMilestoneGoalId: subjectsToEnroll.studyPlanMilestoneGoalId,
        milestoneScope: STUDY,
        milestoneId: 144,
        lowerBound: getValueOrNull(sheetData[17]?.[3]), // Cell D18
        upperBound: null,
      },
      {
        studyPlanMilestoneGoalId: subjectsToTreat.studyPlanMilestoneGoalId,
        milestoneScope: STUDY,
        milestoneId: 147,
        lowerBound: getValueOrNull(sheetData[18]?.[3]), // Cell D19
        upperBound: null,
      },
      {
        studyPlanMilestoneGoalId: subjectsToComplete.studyPlanMilestoneGoalId,
        milestoneScope: STUDY,
        milestoneId: 150,
        lowerBound: getValueOrNull(sheetData[19]?.[3]), // Cell D20
        upperBound: null,
      },
    ],
  };

  return extractedData;
};

const UploadPlanModal = ({ onContinue, ...props }) => {
  const { studyPlan } = useStudyPlan();
  const { showSnackbar } = useSnackbar();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [sheetData, setSheetData] = useState([]);
  const [newStudyPlanId, setNewStudyPlanId] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [overridenNonEquals, setOverridenNonEquals] = useState([]);
  const [promiseResolver, setPromiseResolver] = useState(null);

  // Function to extract country inputs data - UPDATED to behave like old version
  const extractCountryInputsData = (sheetData, studyPlanTypeDetail, worksheet) => {
    // Process all rows from 28 to 58 but filter out rows without country names
    const countryInputsData = sheetData
      .slice(28, 58)
      .map((row, offset) => ({ row, excelRowNumber: 29 + offset }))
      .filter(({ row }) => {
        const countryName = row?.[1]; // Column B
        return countryName !== undefined && countryName !== null && countryName !== "";
      })
      .map(({ row, excelRowNumber }) => {
        const countryName = row[1]; // B
        // Col C: worksheet cell first (skip empty formula caches), then row array
        const siteIdEffortFromSheet =
          readSiteIdEffortFromWorksheetCell(worksheet, excelRowNumber) ??
          row[2];
        const countryStartUpTimeFrame = row[3]; // D
        const enrollmentRate = row[4]; // E
        const NUM_SITES_ACTIVATED_LOWER = row[5]; // F
        const NUM_SITES_ACTIVATED_UPPER = row[6]; // G
        const NUM_SUBJECTS_SCREENED_LOWER = row[7]; // H
        const NUM_SUBJECTS_SCREENED_UPPER = row[8]; // I
        const screenFailRate = row[9]; // J
        const NUM_SUBJECTS_ENROLLED = row[10]; // K
        const dropOutRate = row[11]; // L
        const NUM_SUBJECTS_TREATED = row[12]; // M
        const NUM_SUBJECTS_COMPLETED = row[13]; // N
        return {
          countryName,
          // Honor Excel Site-ID Effort (col C). Derive only when blank.
          siteIdEffort: resolveSiteIdEffort(
            siteIdEffortFromSheet,
            NUM_SITES_ACTIVATED_LOWER,
            NUM_SITES_ACTIVATED_UPPER,
          ),
          countryStartUpTimeFrame,
          enrollmentRate: enrollmentRate === "" ? null : enrollmentRate,
          NUM_SITES_ACTIVATED_LOWER,
          NUM_SITES_ACTIVATED_UPPER,
          NUM_SUBJECTS_SCREENED_LOWER,
          NUM_SUBJECTS_SCREENED_UPPER,
          screenFailRate,
          NUM_SUBJECTS_ENROLLED,
          dropOutRate,
          NUM_SUBJECTS_TREATED,
          NUM_SUBJECTS_COMPLETED,
        };
      });

    const detail = studyPlanTypeDetail?.toLowerCase() === DURATION;
    const countryNames = countryInputsData?.map((country) => country.countryName);
    const countryBoundValues = countryInputsData?.map((country) => 
      (detail ? country?.NUM_SITES_ACTIVATED_LOWER : country?.NUM_SITES_ACTIVATED_UPPER ?? country?.NUM_SITES_ACTIVATED_LOWER)
    );
    const countryEnrollmentRateSum = countryBoundValues.reduce((result, value) => result + (value || 0), 0);
    return { countryInputsData, countryNames, countryBoundValues, countryEnrollmentRateSum };
  };

  // Function to extract milestones data - UPDATED to behave like old version
  const extractMilestonesData = (sheetData, countryNames) => {
    return sheetData
      .slice(63, 93)
      .map((row, index) => ({
        row,
        index,
        countryName: countryNames[index]
      }))
      .filter(item => item.countryName !== undefined && item.countryName !== null && item.countryName !== "")
      .map(({ row, countryName }) => {
        const [
          ,
          SITE_ID_DATE,
          SITE_ID_COMMENT,
          SUBMISSION_DATE,
          SUBMISSION_COMMENT,
          FIRST_REGULATORY_APPROVAL_DATE,
          FIRST_REGULATORY_APPROVAL_COMMENT,
          FSIV_DATE,
          FSIV_COMMENT,
          FPS_DATE,
          FPS_COMMENT,
          FPI_DATE,
          FPI_COMMENT,
          LPI_DATE,
          LPI_COMMENT,
        ] = row.slice(1);

        return {
          countryName,
          SITE_ID_DATE: formatExcelDate(SITE_ID_DATE),
          SITE_ID_COMMENT,
          SUBMISSION_DATE: formatExcelDate(SUBMISSION_DATE),
          SUBMISSION_COMMENT,
          FIRST_REGULATORY_APPROVAL_DATE: formatExcelDate(
            FIRST_REGULATORY_APPROVAL_DATE,
          ),
          FIRST_REGULATORY_APPROVAL_COMMENT,
          FSIV_DATE: formatExcelDate(FSIV_DATE),
          FSIV_COMMENT,
          FPS_DATE: formatExcelDate(FPS_DATE),
          FPS_COMMENT,
          FPI_DATE: formatExcelDate(FPI_DATE),
          FPI_COMMENT,
          LPI_DATE: formatExcelDate(LPI_DATE),
          LPI_COMMENT,
        };
      });
  };

  // Function to extract activation profiles data - UPDATED to behave like old version
  const extractActivationProfilesData = (sheetData, countryNames, studyPlanTypeDetail) => {
    // Check if it's a Remodel type
    const isRemodelType = ["Remodel (D)", "Remodel (R)"].includes(studyPlanTypeDetail);
    
    const activationProfilesData = sheetData
      .slice(98, 128)
      .map((row, index) => ({
        row,
        index,
        countryName: countryNames[index]
      }))
      .filter(item => item.countryName !== undefined && item.countryName !== null && item.countryName !== "")
      .map(({ row, countryName }) => {
        const [
          OVERRIDE_FLAG,
          ,
          CARD_1_PERCENT,
          CARD_1_DATE,
          CARD_2_PERCENT,
          CARD_2_DATE,
          CARD_3_PERCENT,
          CARD_3_DATE,
          CARD_4_PERCENT,
          CARD_4_DATE,
          CARD_5_PERCENT,
          CARD_5_DATE,
          CARD_6_PERCENT,
          CARD_6_DATE,
          CARD_7_PERCENT,
          CARD_7_DATE,
          CARD_8_PERCENT,
          CARD_8_DATE,
          CARD_9_PERCENT,
          CARD_9_DATE,
          CARD_10_PERCENT,
          CARD_10_DATE,
          CARD_11_PERCENT,
          CARD_11_DATE,
        ] = row.slice(1);

        // For Remodel types, always set overrideFl to 1
        // For other types, use the value from Excel
        let overrideValue;
        if (isRemodelType) {
          overrideValue = 1; // Always 1 for Remodel types
        } else {
          overrideValue = (OVERRIDE_FLAG && OVERRIDE_FLAG.toString().toLowerCase() === "yes") ? 1 : 0;
        }

        return {
          overrideFl: overrideValue,
          countryName,
          CARD_1_PERCENT,
          CARD_1_DATE: formatExcelDate(CARD_1_DATE),
          CARD_2_PERCENT,
          CARD_2_DATE: formatExcelDate(CARD_2_DATE),
          CARD_3_PERCENT,
          CARD_3_DATE: formatExcelDate(CARD_3_DATE),
          CARD_4_PERCENT,
          CARD_4_DATE: formatExcelDate(CARD_4_DATE),
          CARD_5_PERCENT,
          CARD_5_DATE: formatExcelDate(CARD_5_DATE),
          CARD_6_PERCENT,
          CARD_6_DATE: formatExcelDate(CARD_6_DATE),
          CARD_7_PERCENT,
          CARD_7_DATE: formatExcelDate(CARD_7_DATE),
          CARD_8_PERCENT,
          CARD_8_DATE: formatExcelDate(CARD_8_DATE),
          CARD_9_PERCENT,
          CARD_9_DATE: formatExcelDate(CARD_9_DATE),
          CARD_10_PERCENT,
          CARD_10_DATE: formatExcelDate(CARD_10_DATE),
          CARD_11_PERCENT,
          CARD_11_DATE: formatExcelDate(CARD_11_DATE),
        };
      });
    
    const profileBoundValues = activationProfilesData.map((profile) => {
      // Get all CARD_X_PERCENT properties and their values
      const cardPercentages = CARD_PERCENTAGES.filter(cardKey => {
        const value = profile[cardKey];
        return value !== null && value !== undefined && value !== "";
      }).map(cardKey => ({
        cardKey,
        value: profile[cardKey]
      }));

      // Get the last card with value
      const lastCard = cardPercentages.length > 0
        ? cardPercentages[cardPercentages.length - 1]
        : null;

      return {
        countryName: profile.countryName,
        overrideFl: profile.overrideFl,
        lastCardKey: lastCard?.cardKey || null,
        lastCardValue: lastCard?.value || null
      };
    });
    return { activationProfilesData, profileBoundValues };
  };

  // Function to extract enrollment seasonal variations data - UPDATED to behave like old version
  const extractEnrollmentSeasonalVariations = (sheetData, countryNames) => {
    return sheetData
      .slice(131, 161)
      .map((row, index) => ({
        row,
        countryName: countryNames[index]
      }))
      .filter(item => item.countryName !== undefined && item.countryName !== null && item.countryName !== "")
      .map(({ row, countryName }) => ({
        countryName,
        MONTH_1: row[2],
        MONTH_2: row[3],
        MONTH_3: row[4],
        MONTH_4: row[5],
        MONTH_5: row[6],
        MONTH_6: row[7],
        MONTH_7: row[8],
        MONTH_8: row[9],
        MONTH_9: row[10],
        MONTH_10: row[11],
        MONTH_11: row[12],
        MONTH_12: row[13],
      }));
  };

  // Function to extract site fatigue data - UPDATED to behave like old version
  const extractSiteFatigue = (sheetData, countryNames) => {
    return sheetData
      .slice(164, 194)
      .map((row, index) => ({
        row,
        countryName: countryNames[index]
      }))
      .filter(item => item.countryName !== undefined && item.countryName !== null && item.countryName !== "")
      .map(({ row, countryName }) => ({
        countryName,
        SITE_FATIGUE_DAYS: row[2],
        SITE_FATIGUE_PERCENT: row[3],
      }));
  };

  const processBoundValues = (countryBoundValues, profileBoundValues, activationProfilesData, isRemodelType) => {
    // For Remodel types, skip the warning dialog since override is always 1
    if (isRemodelType) {
      return Promise.resolve({ shouldContinue: true, updatedProfiles: activationProfilesData });
    }
    
    const conflictingProfiles = profileBoundValues.filter((profile, index) => {
      const countryValue = countryBoundValues[index];
      return profile?.overrideFl === 1 && countryValue !== profile?.lastCardValue;
    });

    return new Promise((resolve) => {
      if (conflictingProfiles.length > 0) {
        // Store the resolver function to call it later from handleWarningConfirm/Cancel
        setPromiseResolver(() => resolve);
        setOverridenNonEquals(conflictingProfiles);
        setShowWarning(true);
        // Store the activation profiles data for later use
        window.tempActivationProfiles = activationProfilesData;
      } else {
        resolve({ shouldContinue: true, updatedProfiles: activationProfilesData }); // No conflicts, continue immediately
      }
    });
  };

  const handleWarningConfirm = () => {
    let finalActivationProfiles = window.tempActivationProfiles;
    
    if (overridenNonEquals.length > 0) {
      const updatedActivationProfiles = window.tempActivationProfiles.map((profile) => {
        const isConflicting = overridenNonEquals.some(
          (conflicted) => conflicted.countryName === profile.countryName
        );
        
        if (isConflicting) {
          return { ...profile, overrideFl: 0 };
        }
        return profile;
      });
      
      finalActivationProfiles = updatedActivationProfiles;
    }
    
    setShowWarning(false);
    
    // Clean up temporary data
    delete window.tempActivationProfiles;
    
    // Resolve the promise with the updated activation profiles
    if (promiseResolver) {
      promiseResolver({ shouldContinue: true, updatedProfiles: finalActivationProfiles });
      setPromiseResolver(null);
    }
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
    setOverridenNonEquals([]);
    setLoading(false);
    setStatus("");
    setProgress(0);
    
    // Clean up temporary data
    delete window.tempActivationProfiles;
    
    // Resolve the promise with false to stop upload
    if (promiseResolver) {
      promiseResolver({ shouldContinue: false, updatedProfiles: null });
      setPromiseResolver(null);
    }
  };

  const handleChange = async (files) => {
    if (files.length === 0) return;
    handleUpload(files);
  };

  const handleUpload = async (files) => {
    if (files.length === 0) return;

    setLoading(true);
    setProgress(0);
    setStatus("Uploading plan...");

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        // defval keeps column indexes stable when Site ID Effort (col C) is blank
        const sheetData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: null,
        });

        setSheetData(sheetData);

        // First create and send additionalFields
        const additionalFields = extractAdditionalFields(sheetData, studyPlan);
        const pauseValidationErrors = validatePauseEnrollmentFields(
          sheetData[22]?.[3], // pauseEnrollmentAfterPatient (Cell D23)
          sheetData[23]?.[3]  // pauseEnrollmentDays (Cell D24)
        );

        if (pauseValidationErrors.length > 0) {
          pauseValidationErrors.forEach(errorMessage => {
            showSnackbar(errorMessage, "error");
          });
          setLoading(false);
          return;
        }
        try {
          // First API call - updatePlan
          const updateResponse = await API.updatePlan(
            additionalFields,
            studyPlan.studyId,
          );

          if (updateResponse.status === 200 &&
            updateResponse.data[0].returnCode === "200"
          ) {
            // Get the new study ID from the response
            const newStudyPlanId = updateResponse.data[0].Id;
            setNewStudyPlanId(updateResponse.data[0].Id);

            // Check if it's a Remodel type
            const isRemodelType = ["Remodel (D)", "Remodel (R)"].includes(studyPlan?.studyPlanTypeDetail);

            // Extract data using the helper functions
            const {
              countryInputsData,
              countryNames,
              countryBoundValues,
            } = extractCountryInputsData(
              sheetData,
              studyPlan?.studyPlanTypeDetail,
              worksheet,
            );
            const milestonesData = extractMilestonesData(sheetData, countryNames);
            // Pass studyPlanTypeDetail to the extraction function
            const { activationProfilesData, profileBoundValues } = extractActivationProfilesData(
              sheetData, 
              countryNames, 
              studyPlan?.studyPlanTypeDetail
            );

            // Validate activation profiles
            const validationResult = validateActivationProfilesTable(activationProfilesData);

            // If there are validation errors, stop the process and show errors
            if (validationResult.hasErrors) {
              validationResult.errorMessages.forEach(errorMessage => {
                showSnackbar(`Activation Profile Error: ${errorMessage}`, "error");
              });
              setLoading(false);
              return;
            }

            // Check if upload should continue based on bound values comparison
            const result = await processBoundValues(
              countryBoundValues, 
              profileBoundValues, 
              activationProfilesData, 
              isRemodelType
            );
            
            if (!result.shouldContinue) {
              // User cancelled, stop execution
              return;
            }

            // Use the updated profiles from the result
            const finalActivationProfiles = result.updatedProfiles;
            const enrollmentSeasonalVariations = extractEnrollmentSeasonalVariations(sheetData, countryNames);
            const siteFatigue = extractSiteFatigue(sheetData, countryNames);

            const payload = {
              studyId: studyPlan.studyId, // Use the new study ID from updateResponse
              studyPlanId: newStudyPlanId,
              studyPlanType: studyPlan.studyPlanType,
              tables: [
                {
                  tableName: STUDY_PLAN_CONSTANTS.TABLE_NAMES.COUNTRY_INPUTS,
                  countries: countryInputsData,
                },
                {
                  tableName: STUDY_PLAN_CONSTANTS.TABLE_NAMES.MILESTONES,
                  countries: milestonesData,
                },
                {
                  tableName: STUDY_PLAN_CONSTANTS.TABLE_NAMES.ACTIVATION_PROFILES,
                  countries: finalActivationProfiles,
                },
                {
                  tableName: STUDY_PLAN_CONSTANTS.TABLE_NAMES.ENROLLMENT_SEASONAL_VARIATIONS,
                  countries: enrollmentSeasonalVariations,
                },
                {
                  tableName: STUDY_PLAN_CONSTANTS.TABLE_NAMES.SITE_FATIGUE,
                  countries: siteFatigue,
                },
              ],
            };

            try {
              // Second API call using the new study ID
              const uploadResponse = await API.uploadPlan(
                studyPlan.studyId,
                payload,
              );
              const data = uploadResponse?.data?.[0] ?? {};
              const returnCode = Number(data.returnCode ?? 200);
              const isSuccess = uploadResponse.status === 200 && returnCode === 200;
              const responseMessage = data.returnDesc || (isSuccess ? "Plan uploaded successfully." : "Plan upload failed.");
              if (isSuccess) {
                // EnrollmentPlanUpload may ignore/recompute Effort from sites.
                // Apply Excel col C via CountryEdit/Add so UI matches the sheet.
                try {
                  const planRes = await API.getStudyPlan(newStudyPlanId);
                  const uploadedPlan = planRes?.data?.[0];
                  const uploadedCountries = uploadedPlan?.countries || [];

                  for (const input of countryInputsData) {
                    if (!input?.siteIdEffort) continue;

                    const match = uploadedCountries.find((country) => {
                      const name =
                        country?.siteGroups?.[0]?.siteGroupName ||
                        country?.countryName;
                      return name === input.countryName;
                    });
                    const sg = match?.siteGroups?.[0];
                    if (!sg?.siteGroupId) continue;
                    if (sg.timeSiteIdSize === input.siteIdEffort) continue;

                    await API.postSiteGroup(
                      {
                        studyPlanId: newStudyPlanId,
                        siteGroupId: sg.siteGroupId,
                        NUM_SITES_ACTIVATED_LOWER:
                          input.NUM_SITES_ACTIVATED_LOWER ??
                          sg.parameters?.[0]?.lowerBound,
                        NUM_SITES_ACTIVATED_UPPER:
                          input.NUM_SITES_ACTIVATED_UPPER ??
                          sg.parameters?.[0]?.upperBound,
                        siteIdEffort: input.siteIdEffort,
                        countryStartUpTimeFrame:
                          input.countryStartUpTimeFrame ??
                          sg.timeCountryStartUpSize,
                      },
                      studyPlan.studyId,
                    );
                  }
                } catch (effortSyncError) {
                  console.error(
                    "Failed to sync Site ID Effort after upload:",
                    effortSyncError,
                  );
                }

                showSnackbar(
                  responseMessage,
                  resolveApiMessageSeverity(data),
                );
                setProgress(100);
                setStatus("Upload completed.");
                setApiStatus(uploadResponse.status);
              } else {
                showSnackbar(responseMessage, "warning");
                setProgress(100);
                setStatus("Upload failed.");
                setApiStatus(uploadResponse.status);
                setSheetData(data.returnDesc);
              }
            } catch (uploadError) {
              console.error("Upload API Error:", uploadError);
              showSnackbar("Plan upload failed", "error");
              setProgress(100);
              setStatus("Upload failed.");
            }
          } else {
            showSnackbar("Failed to update plan", "error");
            setProgress(100);
            setStatus("Update failed.");
            setApiStatus(updateResponse.data[0].returnCode);
            setSheetData(updateResponse.data[0].returnDesc);
          }
        } catch (error) {
          console.error("API Error:", error);
          showSnackbar("Failed to update plan", "error");
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(files[0]);
    } catch (error) {
      console.error("File Reading Error:", error);
      showSnackbar("Failed to read file", "error");
      setLoading(false);
    }
  };

  const lastContinue = () => {
    props.onClose();
    // Pass the newStudyPlanId to the onContinue callback
    onContinue(newStudyPlanId);
  };

  return (
    <CustomModal
      size={700}
      {...props}
      onClose={loading ? undefined : props.onClose}
      aria-labelledby="Upload Plan"
      aria-describedby="upload-plan"
      title="Upload Plan"
    >
      <Container>
        {!loading && (
          <VContainer>
            <FileUploader
              acceptedFiles={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [],
              }}
              onFileUpload={(event) => handleChange(event)}
            />
          </VContainer>
        )}
        {loading && (
          <VContainer>
            {status !== "Upload completed." && status !== "Upload failed." && (
              <LinearProgress
                variant="determinate"
                value={progress}
              />
            )}
            <div>{status}</div>
            {apiStatus !== 200 && progress === 100 && (
              <>
                <div>
                  System Response:
                  <br />
                  {sheetData}
                </div>
              </>
            )}
            <ButtonContainer>
              {apiStatus === 200 ? (
                <PrimaryButton onClick={lastContinue}>Continue</PrimaryButton>
              ) : (
                <OutlinedButton onClick={props.onClose}>Cancel</OutlinedButton>
              )}
            </ButtonContainer>
          </VContainer>
        )}
        {!loading && (
          <ButtonContainer>
            <OutlinedButton onClick={props.onClose}>Cancel</OutlinedButton>
          </ButtonContainer>
        )}
      
        <WarningDialog
          open={showWarning}
          onConfirm={handleWarningConfirm}
          onCancel={handleWarningCancel}
          message={ACTIVATION_PROFILES_WARNING_MESSAGE_UPLOAD}
        />
      </Container>
    </CustomModal>
  );
};

export default UploadPlanModal;

const VContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding-top: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding: 1em;
  padding-bottom: 0em;
  padding-top: 0;
  max-height: 75vh;
  overflow-y: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
  margin-bottom: 0.5em;
`;